import React, { createContext, useCallback, useContext, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { precacheCustomNamesFromDescriptions } from '../hooks/usePlanningCustomName';

/**
 * Default state for segmentation
 * - isLoaded: boolean indicating if a segmentation has been loaded
 * - firstLoadedDisplaySet: the displaySet object of the first loaded segmentation
 * - hydrateDialogShown: tracks which studies have already shown the hydrate dialog
 */
const DEFAULT_STATE = {
  isLoaded: false,
  firstLoadedDisplaySet: null,
  hydrateDialogShown: new Set(),
  isThumbnailSelectionLoading: false,
  thumbnailCustomNamesByPlanningId: {},
  thumbnailCustomNamesLoading: false,
};

export const SegmentationStateContext = createContext(DEFAULT_STATE);

/**
 * Hook to use the segmentation state context
 * @returns {Object} - state and actions for segmentation state
 */
export const useSegmentationState = () => {
  const context = useContext(SegmentationStateContext);

  if (!context) {
    throw new Error('useSegmentationState must be used within a SegmentationStateProvider');
  }

  return context;
};

/**
 * SegmentationStateProvider Component
 *
 * This provider centralizes the segmentation state instead of using localStorage.
 * Maintains:
 * - isLoaded: whether a segmentation has been loaded
 * - firstLoadedDisplaySet: the displaySet of the first loaded segmentation
 */
export function SegmentationStateProvider({ children }) {
  const thumbnailCustomNamesRequestRef = useRef({
    inFlightSignature: null,
    lastCompletedSignature: null,
  });

  // Reducer to handle state actions
  const reducer = (state, action) => {
    switch (action.type) {
      case 'SET_LOADED': {
        // Updates whether segmentation is loaded
        return {
          ...state,
          isLoaded: action.payload,
        };
      }

      case 'SET_FIRST_DISPLAY_SET': {
        // Saves the displaySet of the first loaded segmentation
        return {
          ...state,
          firstLoadedDisplaySet: action.payload,
        };
      }

      case 'SET_HYDRATE_DIALOG_SHOWN': {
        // Tracks which studies have shown the hydrate dialog
        const newSet = new Set(state.hydrateDialogShown);
        newSet.add(action.payload);
        return {
          ...state,
          hydrateDialogShown: newSet,
        };
      }

      case 'SET_THUMBNAIL_SELECTION_LOADING': {
        return {
          ...state,
          isThumbnailSelectionLoading: action.payload,
        };
      }

      case 'SET_THUMBNAIL_CUSTOM_NAMES': {
        return {
          ...state,
          thumbnailCustomNamesByPlanningId: action.payload,
        };
      }

      case 'SET_THUMBNAIL_CUSTOM_NAMES_LOADING': {
        return {
          ...state,
          thumbnailCustomNamesLoading: action.payload,
        };
      }

      case 'RESET': {
        // Resets state to default values
        return DEFAULT_STATE;
      }

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);

  /**
   * Marks segmentation as loaded
   * @param {boolean} isLoaded - Indicates if segmentation is loaded
   */
  const setIsLoaded = useCallback(
    isLoaded => {
      dispatch({
        type: 'SET_LOADED',
        payload: isLoaded,
      });
    },
    [dispatch]
  );

  /**
   * Saves the displaySet of the first loaded segmentation
   * @param {Object} displaySet - The segmentation displaySet
   */
  const setFirstLoadedDisplaySet = useCallback(
    displaySet => {
      dispatch({
        type: 'SET_FIRST_DISPLAY_SET',
        payload: displaySet,
      });
    },
    [dispatch]
  );

  /**
   * Marks a study as having shown the hydrate dialog
   * @param {string} studyKey - The study key (from StudyInstanceUID or SeriesInstanceUID)
   */
  const setHydrateDialogShown = useCallback(
    studyKey => {
      dispatch({
        type: 'SET_HYDRATE_DIALOG_SHOWN',
        payload: studyKey,
      });
    },
    [dispatch]
  );

  /**
   * Checks if hydrate dialog has been shown for a study
   * @param {string} studyKey - The study key to check
   * @returns {boolean} - True if dialog has been shown for this study
   */
  const hasHydrateDialogBeenShown = useCallback(
    studyKey => {
      return state.hydrateDialogShown.has(studyKey);
    },
    [state.hydrateDialogShown]
  );

  const setThumbnailSelectionLoading = useCallback(
    isLoading => {
      dispatch({
        type: 'SET_THUMBNAIL_SELECTION_LOADING',
        payload: isLoading,
      });
    },
    [dispatch]
  );

  const precacheThumbnailCustomNames = useCallback(
    async descriptions => {
      if (!descriptions || descriptions.length === 0) {
        thumbnailCustomNamesRequestRef.current.inFlightSignature = null;
        thumbnailCustomNamesRequestRef.current.lastCompletedSignature = null;
        dispatch({
          type: 'SET_THUMBNAIL_CUSTOM_NAMES',
          payload: {},
        });
        dispatch({
          type: 'SET_THUMBNAIL_CUSTOM_NAMES_LOADING',
          payload: false,
        });
        return;
      }

      const descriptionsSignature = [...new Set(descriptions.filter(Boolean))]
        .map(value => String(value).trim())
        .filter(Boolean)
        .sort()
        .join('|');

      if (!descriptionsSignature) {
        return;
      }

      if (
        thumbnailCustomNamesRequestRef.current.inFlightSignature === descriptionsSignature ||
        thumbnailCustomNamesRequestRef.current.lastCompletedSignature === descriptionsSignature
      ) {
        return;
      }

      thumbnailCustomNamesRequestRef.current.inFlightSignature = descriptionsSignature;

      dispatch({
        type: 'SET_THUMBNAIL_CUSTOM_NAMES_LOADING',
        payload: true,
      });

      try {
        const customNames = await precacheCustomNamesFromDescriptions(descriptions);

        dispatch({
          type: 'SET_THUMBNAIL_CUSTOM_NAMES',
          payload: customNames,
        });

        thumbnailCustomNamesRequestRef.current.lastCompletedSignature = descriptionsSignature;
      } finally {
        if (thumbnailCustomNamesRequestRef.current.inFlightSignature === descriptionsSignature) {
          thumbnailCustomNamesRequestRef.current.inFlightSignature = null;
        }

        dispatch({
          type: 'SET_THUMBNAIL_CUSTOM_NAMES_LOADING',
          payload: false,
        });
      }
    },
    [dispatch]
  );

  /**
   * Resets the segmentation state
   */
  const reset = useCallback(() => {
    dispatch({
      type: 'RESET',
    });
  }, [dispatch]);

  const value = {
    // State
    isLoaded: state.isLoaded,
    firstLoadedDisplaySet: state.firstLoadedDisplaySet,
    hydrateDialogShown: state.hydrateDialogShown,
    isThumbnailSelectionLoading: state.isThumbnailSelectionLoading,
    thumbnailCustomNamesByPlanningId: state.thumbnailCustomNamesByPlanningId,
    thumbnailCustomNamesLoading: state.thumbnailCustomNamesLoading,

    // Actions
    setIsLoaded,
    setFirstLoadedDisplaySet,
    setHydrateDialogShown,
    hasHydrateDialogBeenShown,
    setThumbnailSelectionLoading,
    precacheThumbnailCustomNames,
    reset,
  };

  return (
    <SegmentationStateContext.Provider value={value}>{children}</SegmentationStateContext.Provider>
  );
}

SegmentationStateProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
